/**
 * A refused navigation or request, carrying the HTTP status that caused it.
 *
 * Route resolves and permission guards throw (or reject with) this so the ui-router error
 * hook can route the user — see `AccessDeniedService.handleTransitionError` and
 * docs/PERMISSION-REDIRECT.md.
 *
 */
export class RefusedError extends Error {

    /**
     * Set when the thrower has already validated against the backend and knows the
     * session is dead, so the error hook can expire it outright instead of spending
     * another `/validate` round trip re-deriving what is already known.
     */
    sessionExpired?: boolean;

    /**
     * Human-readable detail, kept for the callers that used to reject with
     * `{ status, data }` and for anything that reads a message off the rejection.
     */
    data?: string;

    constructor(public readonly status: number, message?: string) {
        super(message || `Request refused with status ${status}`);
        this.name = 'RefusedError';
        // Restores the prototype chain so `instanceof` holds. Redundant while the build
        // targets ES6, but free, and the flow plugin's HttpStatusError does the same.
        Object.setPrototypeOf(this, RefusedError.prototype);
    }

    /** No session: the caller must log in. Routes to the login screen with a return URL. */
    static unauthorized(message?: string): RefusedError {
        return new RefusedError(401, message);
    }

    /** Authenticated but not permitted. Routes to the best available landing page. */
    static forbidden(message?: string): RefusedError {
        return new RefusedError(403, message);
    }

    /**
     * A 401 the thrower has already confirmed against the backend — the session really is
     * gone, so the hook should expire it rather than re-validate.
     */
    static expiredSession(message?: string): RefusedError {
        const error = new RefusedError(401, message || 'Session expired');
        error.sessionExpired = true;
        return error;
    }
}
